import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { change_MyData, check_anthScreen } from '../store';
import { LoadingModal } from '../Pages/component/modal/modal';
import { getAdminLoginDestination, isAdminLoginPath } from './loginDestination';
import SessionCheckError from './SessionCheckError';

export default function AdminSessionGate({ children }) {
    const location = useLocation();
    const dispatch = useDispatch();
    const savedUser = useSelector(state => state.MyData);
    const savedUserRef = useRef(savedUser);
    savedUserRef.current = savedUser;
    const isLoginPath = isAdminLoginPath(location.pathname);
    const scope = isLoginPath ? `login:${location.key}` : 'app';
    const verified = useRef(false);
    const [retry, setRetry] = useState(0);
    const [session, setSession] = useState({ scope: null, status: 'checking' });

    useEffect(() => {
        // Check once on startup and again on each login-page entry. Changes to
        // local user data during PIN/SMS login must not restart this check.
        if (!isLoginPath && verified.current) return;
        verified.current = false;
        const controller = new AbortController();
        let active = true;
        setSession({ scope, status: 'checking' });

        async function checkSession() {
            try {
                const response = await axios.get(process.env.REACT_APP_API + '/api/admin/me', {
                    withCredentials: true,
                    signal: controller.signal,
                    timeout: 10000,
                });
                if (!active) return;
                const result = response?.data;
                if (result?.code === 1000) {
                    // /admin/me confirms the session with code 1000 and data {}.
                    // Profile fields are optional and do not determine login status.
                    const profile = result.data?.user ?? result.data;
                    const hasProfile = profile && typeof profile.role === 'string' && profile.role.length > 0;
                    const user = hasProfile ? profile : savedUserRef.current;
                    if (hasProfile) dispatch(change_MyData(profile));
                    verified.current = true;
                    setSession({ scope, status: 'authenticated', user });
                } else if (result?.code === 9999) {
                    dispatch(change_MyData(null));
                    dispatch(check_anthScreen([]));
                    verified.current = true;
                    setSession({ scope, status: 'anonymous' });
                } else {
                    throw new Error('Unexpected session response');
                }
            } catch (error) {
                if (active) setSession({ scope, status: 'error' });
            }
        }
        checkSession();
        return () => {
            active = false;
            controller.abort();
        };
    }, [dispatch, isLoginPath, scope, retry]);

    // Hide the previous result immediately, before the entry effect runs.
    if ((isLoginPath && session.scope !== scope) || session.status === 'checking') return <LoadingModal />;
    if (session.status === 'error') return <SessionCheckError onRetry={() => setRetry(value => value + 1)} />;
    if (isLoginPath && session.status === 'authenticated') {
        return <Navigate to={getAdminLoginDestination(session.user)} replace />;
    }
    if (!isLoginPath && session.scope === 'app' && session.status === 'anonymous') {
        return <Navigate to="/" replace />;
    }
    return children;
}
